/**
 * CRM v2 — Reciclagem de leads (R3): store React Query da configuração.
 *
 * Contrato: GET/PUT /api/v1/crm/config/reciclagem — payload { config }.
 * Convenções do repo (ver crm-store.ts): queryKey prefixo 'v1', mutations
 * invalidam as keys tocadas. Arquivo próprio pra não colidir com o
 * crm-store.ts (dono: outro agente da rodada).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from '@/lib/api';

export interface ReciclagemConfig {
  /** Liga/desliga a reciclagem inteira. */
  ativo: boolean;
  /** Dias parado pra liberar um lead com status Novo. */
  diasLiberarNovo: number;
  /** Dias parado pra liberar um lead com status Em contato. */
  diasLiberarEmContato: number;
  /** Quantos dias antes de liberar o dono recebe o aviso. */
  avisoDias: number;
  /** pool = fica disponível pra qualquer vendedor; redistribuicao = vai direto pro vendedor com menos leads. */
  modo: 'pool' | 'redistribuicao';
  /** Dias que um vendedor espera pra pegar de novo um lead que já foi dele (anti-ping-pong). */
  cooldownDias: number;
  /** Máximo de leads por vendedor. 0 = sem limite. */
  tetoPorVendedor: number;
}

const KEY = ['v1', 'crm', 'config', 'reciclagem'] as const;

/**
 * Normaliza o texto digitado num campo de dias/quantidade: inteiro >= 0.
 * Valor inválido (vazio, negativo, não numérico) devolve `null` — o
 * chamador decide manter o valor anterior.
 */
export function normalizarInteiroNaoNegativo(texto: string): number | null {
  const limpo = texto.trim();
  if (limpo === '') return null;
  const n = Number(limpo);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

export function useReciclagemConfig() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<{ config: ReciclagemConfig }>('/api/v1/crm/config/reciclagem'),
    enabled: hasToken(),
    staleTime: 60_000,
    select: (data) => data.config,
  });
}

export function useReciclagemConfigActions() {
  const qc = useQueryClient();
  const salvar = useMutation({
    mutationFn: (body: Partial<ReciclagemConfig>) =>
      api.put<{ config: ReciclagemConfig }>('/api/v1/crm/config/reciclagem', body),
    onSuccess: (data) => {
      // O PUT devolve a config atualizada — grava direto e revalida.
      qc.setQueryData(KEY, data);
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
  return { salvar };
}
